import { SearchableTree } from "@/shared-components/SearchableTree";
import styles from "./FolderSearchableTree.module.scss";
import { useEffect, useState } from "react";
import { IPanelScope, ITreeItem } from "@/interfaces";
import { FoldersService } from "@/services";
import { GENERIC_ERROR_MESSAGE } from "@/constants";
import { findNodeById } from "@/utils/common";
import { SearchService } from "@/services";

interface GenericSearchableTreeProps {
  scope: IPanelScope;
  selectedItemId: string;
  onChange: (id: string) => void;
}

export const FolderSearchableTree = ({
  scope,
  selectedItemId,
  onChange,
}: GenericSearchableTreeProps) => {
  const [treeItems, setTreeItems] = useState<ITreeItem[]>([]);
  const [error, setError] = useState<string | undefined>();
  const [selectedItem, setSelectedItem] = useState<ITreeItem | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const buildAncestryTree = async (folders: ITreeItem[]) => {
    if (!selectedItemId) return;

    const selectedFolder = await FoldersService.fetchFolder(
      scope,
      selectedItemId,
    );
    // ToDo: fix typescript error
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const ancestry = selectedFolder.serverObject.ancestry?.split("/") || [];

    for (let i = 0; i < ancestry.length; i++) {
      const ancestryId = ancestry[i];
      const children = await FoldersService.getFolderChildren(
        scope,
        ancestryId,
      );

      const currFolder = findNodeById(folders, ancestryId);

      if (currFolder) {
        currFolder.children = children;
      }
    }
  };

  const fetchFolders = async () => {
    try {
      setIsLoading(true);

      const { items } = await FoldersService.fetchFoldersFromServer(scope);

      await buildAncestryTree(items);
      setTreeItems(items);
      setSelectedItem(findNodeById(items, selectedItemId));
    } catch (err) {
      console.error("fetchFolders->Failure fetch folders", err);
      setError(GENERIC_ERROR_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFolders();
  }, []);

  const handleSelected = (node: ITreeItem) => {
    setSelectedItem(node);
    onChange(node.id);
  };

  const handleNodeExpand = async (node: ITreeItem) => {
    node.extra.isLoading = true;
    node.extra.error = null;
    try {
      const children = await FoldersService.getFolderChildren(scope, node.id);
      node.extra.isLoading = false;
      node.children = [...children];
    } catch (e) {
      console.error(`FolderSearchableTree=>handleNodeExpand`, e);
      node.extra.error = GENERIC_ERROR_MESSAGE;
    } finally {
      node.extra.isLoading = false;
      setTreeItems([...treeItems]);
    }
  };

  const handleSearch = async (criteria: string) => {
    try {
      setIsLoading(true);
      return await SearchService.serverSearch(scope, criteria);
    } catch (err) {
      console.error("handleSearch->Failure search folders", err);
      setError(GENERIC_ERROR_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.GenericSearchableTree}>
      <SearchableTree
        isLoading={isLoading}
        width={"100%"}
        customClassName={styles.CustomSearchableTree}
        items={treeItems}
        error={error}
        selectedItem={selectedItem}
        scope={scope}
        handleSelected={handleSelected}
        handleNodeExpand={handleNodeExpand}
        handleSearch={handleSearch}
      />
    </div>
  );
};
